import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Popconfirm,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DocumentPreviewModal from "../BookingsAdmin/DocumentPreviewModal";
import {
  deleteMasterContainerDocument,
  getMasterContainerDocument,
  getMasterLinkForContainer,
  uploadAndSaveMasterContainerDocument,
  type MasterContainerDocument,
} from "../../services/masterContainerDocumentsService";
import {
  downloadContainerDocument,
  getContainerDocumentPreviewUrl,
  getFileType,
} from "../../utils/containerDocumentsUtils";

const { Text } = Typography;

export interface MasterContainerAwbBlSectionProps {
  masterContainerId: number;
  masterReference?: string | null;
  /** When false, only show status + download (no upload/delete). */
  editable?: boolean;
  style?: React.CSSProperties;
  onDocumentChange?: () => void;
}

export const MasterContainerAwbBlSection: React.FC<
  MasterContainerAwbBlSectionProps
> = ({
  masterContainerId,
  masterReference,
  editable = true,
  style,
  onDocumentChange,
}) => {
  const [document, setDocument] = useState<MasterContainerDocument | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fetchDocument = useCallback(async () => {
    setLoading(true);
    const result = await getMasterContainerDocument(masterContainerId);
    if (result.error) {
      message.error("Failed to load AWB/BL document");
      setDocument(null);
    } else {
      setDocument(result.data);
    }
    setLoading(false);
  }, [masterContainerId]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadAndSaveMasterContainerDocument(
        masterContainerId,
        file
      );
      if (!result.success || result.error) {
        throw result.error ?? new Error("Upload failed");
      }
      message.success(
        document ? "AWB/BL replaced" : "AWB/BL uploaded"
      );
      setDocument(result.data);
      onDocumentChange?.();
    } catch (err) {
      console.error(err);
      message.error("Failed to upload AWB/BL");
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async () => {
    if (!document) return;
    const fileInfo = getFileType(document.file_name);
    if (fileInfo.type === "other") {
      await downloadContainerDocument(document.file_path, document.file_name);
      return;
    }
    setPreviewVisible(true);
    const url = await getContainerDocumentPreviewUrl(document.file_path);
    if (!url) {
      message.error("Failed to load preview");
      setPreviewVisible(false);
      return;
    }
    setPreviewUrl(url);
  };

  const handleDelete = async () => {
    const result = await deleteMasterContainerDocument(masterContainerId);
    if (!result.success) {
      message.error("Failed to delete AWB/BL");
      return;
    }
    message.success("AWB/BL removed");
    setDocument(null);
    onDocumentChange?.();
  };

  const title = masterReference
    ? `AWB/BL — ${masterReference}`
    : "AWB/BL";

  return (
    <>
      <Card
        size="small"
        title={title}
        style={style}
        extra={
          editable ? (
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                void handleUpload(file);
                return false;
              }}
            >
              <Button
                size="small"
                icon={<UploadOutlined />}
                loading={uploading}
              >
                {document ? "Replace" : "Upload"}
              </Button>
            </Upload>
          ) : null
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Spin size="small" />
          </div>
        ) : !document ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {editable
              ? "No AWB/BL uploaded yet. Upload one file for this Master BL/AWB."
              : "No AWB/BL on linked Master BL/AWB."}
          </Text>
        ) : (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text ellipsis title={document.file_name} style={{ fontSize: 12 }}>
                {document.file_name}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {dayjs(document.created_at).format("DD/MM/YYYY HH:mm")}
              </Text>
            </div>
            <Space size={0}>
              {getFileType(document.file_name).type !== "other" && (
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => void handlePreview()}
                />
              )}
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() =>
                  downloadContainerDocument(
                    document.file_path,
                    document.file_name
                  )
                }
              />
              {editable && (
                <Popconfirm
                  title="Remove AWB/BL from this master?"
                  onConfirm={() => void handleDelete()}
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              )}
            </Space>
          </Space>
        )}
      </Card>

      <DocumentPreviewModal
        visible={previewVisible}
        onClose={() => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewVisible(false);
          setPreviewUrl(null);
        }}
        fileName={document?.file_name || ""}
        blobUrl={previewUrl}
        onDownload={
          document
            ? () =>
                downloadContainerDocument(
                  document.file_path,
                  document.file_name
                )
            : undefined
        }
      />
    </>
  );
};

/** Read-only AWB/BL on container pages — loads from linked master via container id. */
export const ContainerMasterAwbBlReadOnly: React.FC<{
  containerId: number;
  /** Bump after linking container to a master so the section refetches. */
  refreshToken?: number | string | null;
  style?: React.CSSProperties;
}> = ({ containerId, refreshToken, style }) => {
  const [masterId, setMasterId] = useState<number | null>(null);
  const [masterReference, setMasterReference] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      setResolving(true);
      const result = await getMasterLinkForContainer(containerId);
      if (cancelled) return;
      if (result.error) {
        message.error("Failed to load linked Master BL/AWB");
        setMasterId(null);
        setMasterReference(null);
      } else {
        setMasterId(result.data?.masterContainerId ?? null);
        setMasterReference(result.data?.masterReference ?? null);
      }
      setResolving(false);
    };

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [containerId, refreshToken]);

  if (resolving) {
    return (
      <Card size="small" title="AWB/BL" style={style}>
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <Spin size="small" />
        </div>
      </Card>
    );
  }

  if (!masterId) {
    return (
      <Card size="small" title="AWB/BL" style={style}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Link this container to a Master BL/AWB to show the AWB/BL document
          uploaded on that master.
        </Text>
      </Card>
    );
  }

  return (
    <MasterContainerAwbBlSection
      key={`${masterId}-${refreshToken ?? 0}`}
      masterContainerId={masterId}
      masterReference={masterReference}
      editable={false}
      style={style}
    />
  );
};

export default MasterContainerAwbBlSection;
